import { AppProvider, AppProviderProps, createApiClient } from "@cdv/jazz-ui";
import { createTheme } from "@mui/material";
import { csCZ } from "@mui/material/locale";
import i18next from "i18next";
import { ReactElement, useContext, useEffect, useMemo } from "react";
import { initReactI18next } from "react-i18next";
import { useLocalStorage } from "react-use";
import { fetchExchange } from "urql";
import * as schema from "../../api";
import { CurrentUser, Page, TestDefinition } from "../../api";
import { can } from "../../auth";
import { i18nOptions } from "../../i18n";
import { validator } from "../../validation";
import matchEntries from "../matchEntries";
import { Resource } from "../utils";
import "../utils/global";
import AuthProvider from "./Auth/AuthProvider";
import AuthRouter from "./Auth/Router";
import Router from "./Router";
import { PageContext } from "./Context/PageContext";
import HomepageContent from "../Homepage/Content";

const theme = createTheme({
  components: {
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 60,
          "@media (min-width: 0px)": {
            minHeight: 60,
          },
        },
      },
    },
  },
});

const props: AppProviderProps = {
  env: window.__ENV__,
  can,
  matchEntries,
  locale: "cs",
  schema,
  theme,
  snackbar: {
    anchorOrigin: { horizontal: "center", vertical: "bottom" },
    maxSnack: 3,
    preventDuplicate: true,
  },
  themeLocalization: csCZ,
  validator,
};

export default function App(): ReactElement {
  const [user, setUser] = useLocalStorage<CurrentUser>(Resource.CurrentUser);
  const token = localStorage.getItem("token");
  const { setPages, pages, setTests, tests } = useContext(PageContext);

  i18next.use(initReactI18next).init(i18nOptions);

  const handleSetUser = (user: any) => {
    setUser(user);
  };

  const api = useMemo(() => {
    if (!user) {
      return undefined;
    }

    return createApiClient({
      exchanges: [fetchExchange],
      url: window.__ENV__.API_URL,
      fetchOptions: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
  }, [user, token]);

  props.user = user;
  props.api = api;

  useEffect(() => {
    if (!api) {
      return;
    }

    if (tests.length === 0) {
      api
        .mutation(schema.SyncTestDefinitionsDocument, {})
        .toPromise()
        .then((result) => {
          const tests = result.data?.syncTestDefinitions;

          if (tests) {
            setTests(tests as TestDefinition[]);
          }
        });
    }

    if (pages.length === 0) {
      api
        .mutation(schema.SyncPagesDocument, {})
        .toPromise()
        .then((result) => {
          const pages = result.data?.syncPages;

          if (pages) {
            setPages(pages as Page[]);
          }
        });
    }
  }, [api]);

  if (user) {
    return (
      <AppProvider {...props}>
        <HomepageContent>
          <Router />
        </HomepageContent>
      </AppProvider>
    );
  }

  return (
    <AppProvider {...props}>
      <AuthProvider setUser={handleSetUser}>
        <AuthRouter />
      </AuthProvider>
    </AppProvider>
  );
}
